import { FieldOrigin, fieldOriginOptions, isCustomOrigin } from 'config/loan.input.visibility.type'

import { getLoanInputFields } from './loan.input.fields.constants'

// Path prefixes that identify a loan-scoped screen (so the lookup only shows inside a loan).
const LOAN_ROUTE_PREFIXES = [
  'loan_process',
  'servicing',
  'underwriting',
  'underwriting_summary',
  'review_form',
  'appraisal_information',
  'expiration_dates',
  'dataverify',
  'notes',
  'reo',
  'budget',
  'loanInTake',
  'bytepro_logs',
  'loan_change_request',
  'post_closing',
  'purchase_advice',
  'purchase_advice_seller',
  'closing_calendar',
  'closing_screen',
  'fee_worksheet',
  'amortization_schedule',
  'order_valuation',
  'document_genie',
  'secondary_marketing_screen',
  'national_flood',
  'parties',
  'funding',
  'contractor_application',
  'invoice',
  'fci',
  'lightningDocs',
  'automateLoanDocs',
  'goDocs',
  'hmda',
  'loanEstimate',
  'loanEstimate2',
  'loanEstimate3',
  'closingDisclosure1',
  'closingDisclosureAddendum',
  'creditDenial',
  'drawRequest',
  'borrowingBase',
  'loan_actions',
  'uw_link_page',
  'configurablePage',
  // 'reminders' deliberately omitted: /reminders is a standalone list, and even
  // /reminders/:loanNumber is a reminder list filtered by loan, not a loan detail screen.
]

const LOAN_ROUTE_RE = new RegExp(`^/(${LOAN_ROUTE_PREFIXES.join('|')})(/|$)`)

// True when the given pathname is a loan-scoped screen.
export const isLoanRoute = (pathname: string): boolean =>
  LOAN_ROUTE_RE.test(pathname) || /^\/tasks\/loan\//.test(pathname)

// A single searchable "jump to" destination inside a loan.
export type JumpTarget = {
  id: string
  label: string
  group: string
  keywords?: string
  to: (loanNumber: string | number) => string
}

// Top-level loan screens (each route takes the loan number as its last path segment).
const SCREEN_TARGETS: JumpTarget[] = [
  { id: 'overview', label: 'Loan Overview', group: 'Overview', keywords: 'summary home', to: (n) => `/loan_process/overview/${n}` },
  { id: 'structure', label: 'Loan Structure', group: 'Overview', keywords: 'terms rate amount ltv', to: (n) => `/loan_process/structure/${n}` },
  { id: 'application', label: 'Loan Application', group: 'Application', keywords: 'borrower 1003', to: (n) => `/loan_process/application/${n}` },
  { id: 'submission', label: 'Loan Submission', group: 'Overview', keywords: 'submit documents conditions', to: (n) => `/loan_process/submit/${n}` },

  // Underwriting (these sub-screens have their own deep-linkable routes)
  { id: 'underwriting', label: 'Underwriting', group: 'Underwriting', keywords: 'uw', to: (n) => `/underwriting/${n}` },
  { id: 'uwSummary', label: 'Underwriting Summary', group: 'Underwriting', to: (n) => `/underwriting_summary/${n}` },
  { id: 'reviewForm', label: 'Review Form', group: 'Underwriting', keywords: 'valuation review', to: (n) => `/review_form/${n}` },
  { id: 'appraisalInfo', label: 'Appraisal Information', group: 'Underwriting', keywords: 'value', to: (n) => `/appraisal_information/${n}` },
  { id: 'expiration', label: 'Expiration Dates', group: 'Underwriting', to: (n) => `/expiration_dates/${n}` },
  { id: 'dataverify', label: 'Dataverify', group: 'Underwriting', keywords: '4506 ssa fraud', to: (n) => `/dataverify/${n}` },

  // Standalone loan screens
  { id: 'notes', label: 'Notes', group: 'Screens', keywords: 'email', to: (n) => `/notes/${n}` },
  { id: 'reo', label: 'REO', group: 'Screens', to: (n) => `/reo/${n}` },
  { id: 'budget', label: 'Budget & Scope', group: 'Screens', keywords: 'draw construction rehab', to: (n) => `/budget/${n}` },
  { id: 'loanInTake', label: 'Loan In-Take', group: 'Screens', to: (n) => `/loanInTake/${n}` },
  { id: 'postClosing', label: 'Post Closing', group: 'Screens', to: (n) => `/post_closing/${n}` },
  { id: 'purchaseAdvice', label: 'Purchase Advice', group: 'Screens', to: (n) => `/purchase_advice/${n}` },
  { id: 'closingCalendar', label: 'Closing Calendar', group: 'Screens', to: (n) => `/closing_calendar/${n}` },
  { id: 'closingScreen', label: 'Closing Screen', group: 'Screens', to: (n) => `/closing_screen/${n}` },
  { id: 'feeWorksheet', label: 'Fee Worksheet', group: 'Screens', to: (n) => `/fee_worksheet/${n}` },
  { id: 'amortization', label: 'Amortization Schedule', group: 'Screens', to: (n) => `/amortization_schedule/${n}` },
  { id: 'orderValuation', label: 'Order Valuation', group: 'Screens', to: (n) => `/order_valuation/${n}` },
  { id: 'documentGenie', label: 'Document Genie', group: 'Screens', to: (n) => `/document_genie/${n}` },
  { id: 'secondaryMarketing', label: 'Secondary Marketing', group: 'Screens', keywords: 'lock trade', to: (n) => `/secondary_marketing_screen/${n}` },
  { id: 'nationalFlood', label: 'Flood', group: 'Screens', to: (n) => `/national_flood/${n}` },
  { id: 'parties', label: 'Parties', group: 'Screens', keywords: 'contacts', to: (n) => `/parties/${n}` },
  { id: 'funding', label: 'Funding', group: 'Screens', to: (n) => `/funding/${n}` },
  { id: 'contractorApp', label: 'Contractor Application', group: 'Screens', to: (n) => `/contractor_application/${n}` },
  { id: 'invoice', label: 'Invoice', group: 'Screens', to: (n) => `/invoice/${n}` },
  { id: 'fci', label: 'FCI', group: 'Screens', to: (n) => `/fci/${n}` },
  { id: 'lightningDocs', label: 'Lightning Docs', group: 'Screens', to: (n) => `/lightningDocs/${n}` },
  { id: 'automateLoanDocs', label: 'Automate Loan Docs', group: 'Screens', keywords: 'ald documents', to: (n) => `/automateLoanDocs/${n}` },
  { id: 'goDocs', label: 'GoDocs', group: 'Screens', keywords: 'closing documents godocs', to: (n) => `/goDocs/${n}` },
  { id: 'hmda', label: 'HMDA', group: 'Screens', to: (n) => `/hmda/${n}` },
  { id: 'loanEstimate', label: 'Loan Estimate', group: 'Screens', to: (n) => `/loanEstimate/${n}` },
  { id: 'closingDisclosure', label: 'Closing Disclosure', group: 'Screens', to: (n) => `/closingDisclosure1/${n}` },
  { id: 'creditDenial', label: 'Credit Denial', group: 'Screens', to: (n) => `/creditDenial/${n}` },
  { id: 'drawRequest', label: 'Draw Request', group: 'Screens', to: (n) => `/drawRequest/${n}` },
  { id: 'borrowingBase', label: 'Borrowing Base', group: 'Screens', to: (n) => `/borrowingBase/${n}` },
  { id: 'loanActions', label: 'Loan Actions', group: 'Screens', to: (n) => `/loan_actions/${n}` },
  { id: 'reminders', label: 'Reminders', group: 'Screens', to: (n) => `/reminders/${n}` },
  { id: 'tasks', label: 'Tasks', group: 'Screens', to: (n) => `/tasks/loan/${n}` },
  { id: 'byteproLogs', label: 'Bytepro Logs', group: 'Screens', to: (n) => `/bytepro_logs/${n}` },
  { id: 'loanChangeRequest', label: 'Loan Change Request', group: 'Screens', to: (n) => `/loan_change_request/${n}` },
]

// Servicing hub sub-menus — deep-linkable via ?menu=<key> (mirrors Servicing.tsx).
const SERVICING_MENU: Record<string, string> = {
  servicingNotes: 'Servicing Information',
  rateChanges: 'Rate & Payment Changes',
  ach: 'ACH',
  trustLedger: 'Trust Ledger',
  servicingDocument: 'Servicing Documents',
  loanExtensions: 'Loan Extensions',
  participation: 'Investors',
  defaultTracking: 'Default Tracking',
  notesForServicing: 'Servicing Notes',
  payoff: 'Payoff',
  reinstatement: 'Reinstatement',
  statements: 'Statements',
  loanPayments: 'Loan Payments',
  loanCharges: 'Loan Charges',
  penalties: 'Penalties',
  contacts: 'Contacts',
  mers: 'MERS #',
}

const SERVICING_TARGETS: JumpTarget[] = [
  { id: 'servicing', label: 'Servicing', group: 'Servicing', keywords: 'servicing', to: (n) => `/servicing/${n}` },
  ...Object.entries(SERVICING_MENU).map(([key, label]) => ({
    id: `servicing-${key}`,
    label,
    group: 'Servicing',
    keywords: 'servicing',
    to: (n: string | number) => `/servicing/${n}?menu=${key}`,
  })),
]

// Left-nav key of the application section each field origin is rendered under
// (mirrors getAppBorrowerMenu in pages/LoanApplication/logic).
const APPLICATION_MENU_BY_ORIGIN: Record<string, string> = {
  [FieldOrigin.BorrowerInformation]: 'borrower',
  [FieldOrigin.PropertyInformation]: 'property',
  [FieldOrigin.AdditionalPropertyAddresses]: 'property',
  [FieldOrigin.EmploymentAndIncome]: 'employment',
  [FieldOrigin.AssetLiability]: 'asset',
  [FieldOrigin.TrackRecord]: 'track',
  [FieldOrigin.DeclarationsHMDA]: 'hmda',
  [FieldOrigin.AdditionalMember]: 'additionalMember',
}

// Where a field of the given origin actually lives. Loan Structure fields are edited on the
// Loan Structure screen, not the application, and custom-information origins get their own
// application menu keyed by the origin itself.
const routeForFieldOrigin = (origin: FieldOrigin | string) => {
  if (origin === FieldOrigin.LoanStructure) return (n: string | number) => `/loan_process/structure/${n}`
  const menu = isCustomOrigin(origin) ? origin : APPLICATION_MENU_BY_ORIGIN[origin]
  if (!menu) return (n: string | number) => `/loan_process/application/${n}`
  return (n: string | number) => `/loan_process/application/${n}?menu=${menu}`
}

// Every field label + section header in the loan application, mapped to the screen that edits it.
export const getApplicationFieldTargets = (): JumpTarget[] => {
  // other/custom pull in party fields + admin custom fields so we index the full label set.
  const registry = getLoanInputFields({ other: true, custom: true })
  const targets: JumpTarget[] = []
  const seen = new Set<string>()

  ;(Object.keys(registry) as FieldOrigin[]).forEach((origin) => {
    // Not a loan field: warehouseApproved is pulled in from the user profile inputs.
    if (origin === FieldOrigin.ManageAccount) return
    const groupLabel = fieldOriginOptions[origin] || 'Application'
    const fields = registry[origin] || {}
    const to = routeForFieldOrigin(origin)
    Object.keys(fields).forEach((key) => {
      const field: any = fields[key]
      const title = String(field?.title || '').trim()
      if (!title || field?.inputType === 'alert') return
      const isSection = field?.inputType === 'section'
      const dedupe = `${origin}:${title.toLowerCase()}`
      if (seen.has(dedupe)) return
      seen.add(dedupe)
      targets.push({
        id: `field-${origin}-${key}`,
        label: title,
        group: isSection ? `${groupLabel} (Section)` : `${groupLabel} (Field)`,
        keywords: `application field ${key}`,
        to,
      })
    })
  })

  return targets
}

// Full catalog: screens + servicing sub-menus + application fields/sections.
export const getLoanJumpTargets = (): JumpTarget[] => {
  let fieldTargets: JumpTarget[] = []
  try {
    fieldTargets = getApplicationFieldTargets()
  } catch {
    fieldTargets = []
  }
  return [...SCREEN_TARGETS, ...SERVICING_TARGETS, ...fieldTargets]
}
